import {
  AppError,
  CommonErrorsKey,
  type SupportedChainId,
  contractBook,
  isSupportedChainId,
} from "@eulerxyz/core-sdk"

import type { Config } from "@wagmi/core"
import {
  type Address,
  type Hex,
  type StateOverride,
  erc20Abi,
  getAddress,
  isHex,
  numberToHex,
  publicActions,
  zeroAddress,
} from "viem"
import { encodeFunctionData } from "viem/utils"
import { debugActions } from "../actions/debugActions"
import {
  type TransactionItems,
  isCompoundTransaction,
  isDepositTransactionItem,
  isNativeAsset,
} from "../factories"

export type BalanceOverrideParams = {
  chainId?: number
  transactions: TransactionItems
  address: Address
}

function findIndexOfLargestBigInt(arr: bigint[]) {
  if (arr.length === 0) return -1 // Handle empty array case

  let maxIndex = 0
  let maxValue = arr[0]

  for (let i = 1; i < arr.length; i++) {
    // @ts-ignore - maxValue might be undefined
    if (arr[i] > maxValue) {
      maxValue = arr[i]
      maxIndex = i
    }
  }

  return maxIndex
}

type MemCacheKey = `${SupportedChainId}:${Address}:${Address}`

/// memory balance slots for overrides - chainId -> account -> token -> slot
const balanceSlots = new Map<MemCacheKey, { address: Address; slot: Hex }>()

function shouldSkipCachingForToken(token: Address) {
  return (
    getAddress(token) ===
    getAddress("0x437cc33344a0B27A429f795ff6B469C72698B291")
  )
}

function specialCasingBalanceOverrides(
  stateOverride: StateOverride,
): StateOverride {
  //
  // Special casing for specific contract
  const wM_CONTRACT = "0x437cc33344a0B27A429f795ff6B469C72698B291" as const

  const result: StateOverride = []
  for (const override of stateOverride) {
    // Special case for wM tokens
    if (
      getAddress(override.address) === getAddress(wM_CONTRACT) &&
      override.stateDiff
    ) {
      // Shift values left by 1 bit by multiplying by 2
      const newStateDiff = override.stateDiff.flatMap((diff) => {
        const shiftedValue = `0x${(BigInt(diff.value) << 8n)
          .toString(16)
          .padStart(64, "0")}` as Hex
        return [
          {
            slot: diff.slot,
            value: shiftedValue,
          },
          {
            slot: `0x${(BigInt(diff.slot) + 1n)
              .toString(16)
              .padStart(64, "0")}` as Hex,
            value: shiftedValue,
          },
        ]
      })

      result.push({
        ...override,
        stateDiff: newStateDiff,
      })
    } else {
      result.push(override)
    }
  }
  return result
}

export async function balanceOverrides(
  config: Config,
  { address, transactions, chainId }: BalanceOverrideParams,
) {
  const mainAccountOperations = new Map<Address, bigint>()
  const flatTransactions = transactions.flatMap((tx) =>
    isCompoundTransaction(tx) ? tx.transactions : [tx],
  )
  for (const tx of flatTransactions) {
    if (
      isDepositTransactionItem(tx) &&
      !isNativeAsset(tx.asset) &&
      !tx.isWrap &&
      tx.asset.address
    ) {
      const key = tx.asset.address
      const value = BigInt(tx.value || 0)
      const current = mainAccountOperations.get(key) || 0n
      mainAccountOperations.set(
        getAddress(key),
        value > current ? value : current,
      )
    }
  }

  const tokens = Array.from(mainAccountOperations.entries())

  return getBalanceOverrides(config, address, tokens, chainId)
}

function getDebugClient(config: Config, chainId: number | undefined) {
  return config
    .getClient({ chainId })
    .extend(publicActions)
    .extend(debugActions)
}

/**
 * Get the balance overrides for the given tokens
 * @param config - The config
 * @param address - The address
 * @param tokens - The tokens [token, value]
 * @param chainId - The chain id
 * @returns The balance overrides
 */
export async function getBalanceOverrides(
  config: Config,
  address: Address,
  tokens: [Address, bigint][],
  chainId?: number,
) {
  const stateOverride: StateOverride = []
  const client = getDebugClient(config, chainId)
  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    throw new AppError(CommonErrorsKey.ChainNotSupported, "No chain found")
  }
  const balances = await client.readContract({
    abi: contractBook.utilsLens.abi,
    address: contractBook.utilsLens.address[client.chain.id],
    functionName: "tokenBalances",
    args: [address || zeroAddress, tokens.map(([token]) => token)],
  })

  try {
    for (const [i, [token, value]] of tokens.entries()) {
      const _balance = balances[i]
      const key = `${client.chain.id}:${address}:${token}` as const
      // @ts-ignore - _balance might be undefined
      if (_balance >= value) continue
      const cached = balanceSlots.get(key)
      const valueHex = numberToHex(value, { size: 32 })
      if (cached && !shouldSkipCachingForToken(token)) {
        stateOverride.push({
          address: cached.address,
          stateDiff: [
            {
              slot: cached.slot,
              value: valueHex,
            },
          ],
        })
      } else {
        const slots: { address: Address; slot: Hex }[] = []
        const trace = await client.defaultTracer({
          data: encodeFunctionData({
            abi: erc20Abi,
            functionName: "balanceOf",
            args: [address],
          } as const),
          to: token,
          from: address,
        })

        if (trace[token.toLowerCase()]) {
          // @ts-ignore - Property might not exist
          const { storage } = trace[token.toLowerCase()]
          for (const slot of Object.keys(storage)) {
            if (isHex(slot))
              slots.push({ address: getAddress(token), slot: slot })
          }
        }

        const balances = await Promise.all(
          slots.map((slot) =>
            client
              .readContract({
                abi: erc20Abi,
                address: token,
                functionName: "balanceOf",
                args: [address],
                stateOverride: [
                  {
                    address: slot.address,
                    stateDiff: [
                      {
                        slot: slot.slot,
                        value: valueHex,
                      },
                    ],
                  },
                ],
              })
              .catch(() => 0n),
          ),
        )

        const slot = slots[findIndexOfLargestBigInt(balances)]
        if (slot) {
          stateOverride.push({
            address: slot.address,
            stateDiff: [
              {
                slot: slot.slot,
                value: valueHex,
              },
            ],
          })
          balanceSlots.set(key, slot)
        }
      }
    }
  } catch {}

  return specialCasingBalanceOverrides(stateOverride)
}
